import { addCache } from 'api/caching'
import { getCerebroHeaders } from 'api/Cerebro/headers'
import { orgScopedApiClient } from 'api/orgScopedClient'
import { ARCHIVED } from 'const/resourceStates'
import {
    AdGroup,
    Brand,
    Campaign,
    Keyword,
    KeywordSearchResult,
    NegativeKeyword,
    NonKeywordTarget,
    Portfolio,
    ProductAd,
    ProductAdSearchResult,
    UpdatePayload,
    UserPermission,
} from 'types'
import { SuggestedBid } from 'types/dataSources'

import {
    CerebroApiOptions,
    CerebroBulkUpdateResponse,
    CerebroPaginatedResponse,
    CerebroRequestParams,
    CerebroResourceResponse,
} from '../types'

export const getProfileFeaturePermissions = (
    profileId: string
): Promise<CerebroResourceResponse<UserPermission[]>> =>
    orgScopedApiClient.get(
        `/api/profile/${profileId}/feature_permissions_verbose/`
    )

/**
 * Fetch integrated ad accounts for the current organization
 */
export const getAdAccounts = (
    params: CerebroRequestParams = {},
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<Brand>> =>
    orgScopedApiClient.get(`/api/profiles/`, {
        params,
        headers: getCerebroHeaders(options.headers),
    })
addCache(getAdAccounts)

export const getAdAccount = (
    adAccountId: string
): Promise<CerebroResourceResponse<Brand>> =>
    orgScopedApiClient.get(`/api/profile/${adAccountId}/`)
addCache(getAdAccount)

export const updateAdAccount = (
    adAccountId: string,
    data: Partial<Brand> | Record<string, unknown>
): Promise<CerebroResourceResponse<Brand>> =>
    orgScopedApiClient.patch(`/api/profile/${adAccountId}/`, data)

export const updateAdAccounts = (
    data: UpdatePayload,
    params?: CerebroRequestParams
): Promise<CerebroBulkUpdateResponse<Brand>> =>
    orgScopedApiClient.patch(`/api/profiles/`, data, { params })

export const syncAdAccount = (
    adAccountId: string
): Promise<CerebroResourceResponse<Brand>> =>
    orgScopedApiClient.get(`/api/profile/${adAccountId}/sync/snapshot/`)

export const getPortfolio = (
    portfolioId: string
): Promise<CerebroResourceResponse<Portfolio>> =>
    orgScopedApiClient.get(`/api/portfolio/${portfolioId}/`)

export const getPortfolios = (
    params: CerebroRequestParams,
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<Portfolio>> =>
    orgScopedApiClient.get('/api/portfolios/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const updatePortfolio = (
    portfolioId: string,
    data: Partial<Portfolio> | Record<string, unknown>
): Promise<CerebroResourceResponse<Portfolio>> =>
    orgScopedApiClient.patch(`/api/portfolio/${portfolioId}/`, data)

export const getCampaigns = (
    params: CerebroRequestParams,
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<Campaign>> =>
    orgScopedApiClient.get('/api/campaigns/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getCampaignsAsyncExport = (
    params: CerebroRequestParams
): Promise<CerebroResourceResponse<Blob>> =>
    orgScopedApiClient.get(`/api/campaigns/export/async/`, {
        params,
    })

export const createAmazonCampaign = (
    data: Partial<Campaign> | Record<string, unknown>
): Promise<CerebroResourceResponse<Campaign>> =>
    orgScopedApiClient.post('/api/campaigns/', data)

export const getCampaign = (
    campaignId: string
): Promise<CerebroResourceResponse<Campaign>> =>
    orgScopedApiClient.get(`/api/campaign/${campaignId}/`)

export const updateCampaign = (
    campaignId: string,
    data: Partial<Campaign> | Record<string, unknown>
): Promise<CerebroResourceResponse<Campaign>> =>
    orgScopedApiClient.patch(`/api/campaign/${campaignId}/`, data)

export const updateCampaigns = (
    data: UpdatePayload,
    params?: CerebroRequestParams
): Promise<CerebroBulkUpdateResponse<Campaign>> =>
    orgScopedApiClient.patch('/api/campaigns/', data, { params })

export const getProductAdsSearch = (
    params: CerebroRequestParams = {},
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<ProductAdSearchResult>> =>
    orgScopedApiClient.get('/api/product_ads_search/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getProductAds = (
    params: CerebroRequestParams = {},
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<ProductAd>> =>
    orgScopedApiClient.get(`/api/product_ads/`, {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getProductAd = (
    productId: string
): Promise<CerebroResourceResponse<ProductAd>> =>
    orgScopedApiClient.get(`/api/product_ad/${productId}/`)

export const createProductAd = (
    data: Partial<ProductAd> | Record<string, unknown>
): Promise<CerebroResourceResponse<ProductAd>> =>
    orgScopedApiClient.post(`/api/product_ads/`, data)

export const updateProductAds = (
    data: UpdatePayload
): Promise<CerebroBulkUpdateResponse<ProductAd>> =>
    orgScopedApiClient.patch(`/api/product_ads/`, data)

export const updateProductAd = (
    productId: string,
    data: Partial<ProductAd> | Record<string, unknown>
): Promise<CerebroResourceResponse<ProductAd>> =>
    orgScopedApiClient.patch(`/api/product_ad/${productId}/`, data)

export const getKeywordsSearch = (
    params: CerebroRequestParams = {},
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<KeywordSearchResult>> =>
    orgScopedApiClient.get('/api/keywords_search/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getSPTargetBidRecommendationsV3 = (
    profileId: string,
    data: CerebroRequestParams
): Promise<CerebroResourceResponse<SuggestedBid[]>> =>
    orgScopedApiClient.post(
        `/api/aaa/?endpoint=sp/targets/bid/recommendations&profile_id=${profileId}&api_version=v3`,
        {
            aaa_data: data,
            headers: {
                'Content-Type':
                    'application/vnd.spthemebasedbidrecommendation.v4+json',
            },
        }
    )

// Amazon answers 422 for ad groups without product ads, and the AAA proxy
// passes the raw body through as the first element of the error array.
export const isAdGroupWithoutAsinsResponse = (
    response: CerebroResourceResponse<unknown>
): boolean => {
    const [detail] = Array.isArray(response.data) ? response.data : []
    return (
        response.status === 422 &&
        typeof detail === 'string' &&
        detail.includes('does not have any asins')
    )
}

export const getSDTargetBidRecommendations = (
    profileId: string,
    data: CerebroRequestParams
): Promise<CerebroResourceResponse<SuggestedBid[]>> =>
    orgScopedApiClient.post(
        `/api/aaa/?endpoint=sd/targets/bid/recommendations&profile_id=${profileId}&api_version=v3`,
        {
            aaa_data: data,
            headers: {
                'Content-Type':
                    'application/vnd.sdtargetingrecommendations.v3.2+json',
            },
        }
    )

export const getSBTargetBidRecommendations = (
    profileId: string,
    data: CerebroRequestParams
): Promise<CerebroResourceResponse<SuggestedBid[]>> =>
    orgScopedApiClient.post(
        `/api/aaa/?endpoint=sb/recommendations/bids&profile_id=${profileId}&api_version=v3`,
        data
    )

export const getKeyword = (
    keywordId: string
): Promise<CerebroResourceResponse<Keyword>> =>
    orgScopedApiClient.get(`/api/keyword/${keywordId}/`)

export const createKeyword = (
    data: Partial<Keyword> | Record<string, unknown>
): Promise<CerebroResourceResponse<Keyword>> =>
    orgScopedApiClient.post(`/api/keywords/`, data)

export const updateKeyword = (
    keywordId: string,
    data: Partial<Keyword> | Record<string, unknown>
): Promise<CerebroResourceResponse<Keyword>> =>
    orgScopedApiClient.patch(`/api/keyword/${keywordId}/`, data)

export const updateKeywords = (
    data: UpdatePayload,
    params?: CerebroRequestParams
): Promise<CerebroBulkUpdateResponse<Keyword>> =>
    orgScopedApiClient.patch('/api/keywords/', data, { params })

export const getCampaignNegativeKeywords = (
    params: CerebroRequestParams,
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<NegativeKeyword>> =>
    orgScopedApiClient.get('/api/campaign_negative_keywords/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const createCampaignNegativeKeyword = (
    data: Partial<NegativeKeyword> | Record<string, unknown>
): Promise<CerebroResourceResponse<NegativeKeyword>> =>
    orgScopedApiClient.post(`/api/campaign_negative_keywords/`, data)

export const deleteCampaignNegativeKeyword = (
    keywordId: string
): Promise<CerebroResourceResponse<NegativeKeyword>> =>
    orgScopedApiClient.patch(`/api/campaign_negative_keyword/${keywordId}/`, {
        state: ARCHIVED,
    })

export const getAdGroups = (
    params: CerebroRequestParams,
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<AdGroup>> =>
    orgScopedApiClient.get(`/api/ad_groups/`, {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getAdGroup = (
    adGroupId: string
): Promise<CerebroResourceResponse<AdGroup>> =>
    orgScopedApiClient.get(`/api/ad_group/${adGroupId}/`)

export const updateAdGroup = (
    adGroupId: string,
    params: Partial<AdGroup> | Record<string, unknown>
): Promise<CerebroResourceResponse<AdGroup>> =>
    orgScopedApiClient.put(`/api/ad_group/${adGroupId}/`, params)

export const updateAdGroups = (
    data: UpdatePayload,
    params?: CerebroRequestParams
): Promise<CerebroBulkUpdateResponse<AdGroup>> =>
    orgScopedApiClient.patch('/api/ad_groups/', data, { params })

export const createAdGroup = (
    data: Partial<AdGroup> | Record<string, unknown>
): Promise<CerebroResourceResponse<AdGroup>> =>
    orgScopedApiClient.post('/api/ad_groups/', data)

export const getNegativeKeywords = (
    params: CerebroRequestParams,
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<NegativeKeyword>> =>
    orgScopedApiClient.get('/api/negative_keywords/', {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const createNegativeKeyword = (
    data: Partial<NegativeKeyword> | Record<string, unknown>
): Promise<CerebroResourceResponse<NegativeKeyword>> =>
    orgScopedApiClient.post(`/api/negative_keywords/`, data)

export const deleteNegativeKeyword = (
    keywordId: string
): Promise<CerebroResourceResponse<NegativeKeyword>> =>
    orgScopedApiClient.patch(`/api/negative_keyword/${keywordId}/`, {
        state: ARCHIVED,
    })

export const getKeywordAsyncExport = (
    params: CerebroRequestParams
): Promise<CerebroResourceResponse<Blob>> =>
    orgScopedApiClient.get(`/api/keywords/export/async/`, {
        params,
    })

export const getNegativeKeywordsCreationTemplate = (): Promise<
    CerebroResourceResponse<Blob>
> => orgScopedApiClient.get(`/api/negative_keywords/create_template/`)

export const getTargetAsyncExport = (
    params: CerebroRequestParams
): Promise<CerebroResourceResponse<Blob>> =>
    orgScopedApiClient.get(`/api/targets/export/async/`, {
        params,
    })

export const updateTarget = (
    targetId: string,
    data: Partial<NonKeywordTarget>
): Promise<CerebroResourceResponse<NonKeywordTarget>> =>
    orgScopedApiClient.patch(`/api/target/${targetId}/`, data)

export const updateTargets = (
    data: UpdatePayload,
    params?: CerebroRequestParams
): Promise<CerebroBulkUpdateResponse<NonKeywordTarget>> =>
    orgScopedApiClient.patch('/api/targets/', data, { params })

export const getTargets = (
    params: CerebroRequestParams,
    options: CerebroApiOptions = {}
): Promise<CerebroPaginatedResponse<NonKeywordTarget>> =>
    orgScopedApiClient.get(`/api/targets/`, {
        params,
        headers: getCerebroHeaders(options.headers),
    })

export const getTarget = (
    targetId: string,
    options: CerebroApiOptions = {}
): Promise<CerebroResourceResponse<NonKeywordTarget>> =>
    orgScopedApiClient.get(`/api/target/${targetId}/`, {
        headers: getCerebroHeaders(options.headers),
    })
